/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateFieldMap,
  FormTemplateMapByKey,
  FormTemplateSection,
} from 'data/template/template.types';
import { TlsProfile } from 'data/tlsProfile/tlsProfile.types';
import { SupportedFeaturesAccessFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import { getTagOptions } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import _ from 'lodash';
import { Distributor } from 'data/distributor/distributor.types';

export type ListenersIpAddressPortsType = {
  [key: string]: string[],
}

const XCCA_FIELDS_TO_SHOW = ['keyalgorithm', 'aftype', 'litype'];

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.name.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.hostname.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.aftype.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.litype.readOnly = true;
    (clonedTemplate.main as FormTemplateSection).fields.catype.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

export function adaptTemplateOptions(
  template: FormTemplate,
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  templates: FormTemplateMapByKey,
  tlsProfile?: TlsProfile,
): FormTemplate {
  if (template == null) {
    return template;
  }
  let clonedTemplate = cloneDeep(template);

  if (tlsProfile?.id) {
    clonedTemplate = adaptInitialTlsVersionOptions(template, tlsProfile.intfType, tlsProfile.tlsVersion);
  }

  if (tlsProfile?.tlsType?.toLocaleLowerCase() === 'client' && tlsProfile?.authenType?.toLocaleLowerCase() === 'mutual') {
    (clonedTemplate.main as FormTemplateSection).fields.hostname.disabled = false;
  } else {
    (clonedTemplate.main as FormTemplateSection).fields.hostname.disabled = true;
  }

  if (tlsProfile?.intfType?.toLocaleLowerCase() === 'dtls') {
    (clonedTemplate.main as FormTemplateSection).fields.intfType.options = DTLSintfTypeOptions;
  } else if (tlsProfile?.intfType !== undefined) {
    (clonedTemplate.main as FormTemplateSection).fields.intfType.options = TLSintfTypeOptions;
  }

  const tagOptions = getTagOptions(templates ?? {}, enabledAccessFunctions ?? {});
  (clonedTemplate.main as FormTemplateSection).fields.aftype.options = [
    {
      label: 'Select an AF Type',
      value: '',
    },
    ...tagOptions
  ];

  return clonedTemplate;
}

export function adaptTemplateOnCreate(template: FormTemplate): FormTemplate {
  if (template == null) {
    return template;
  }
  const clonedTemplate = cloneDeep(template);
  const formTemplateSection = clonedTemplate.main as FormTemplateSection;
  clonedTemplate.formId = Date.now().toString();
  if (
    formTemplateSection.metadata.layout &&
    _.isArray(formTemplateSection.metadata.layout.rows)
  ) {
    formTemplateSection.metadata.layout.rows[1] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[1], 3);
    formTemplateSection.metadata.layout.rows[2] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[2], 3);
  }
  return clonedTemplate;
}

export function adaptNetworkIdField(
  templateSection: FormTemplateSection,
  interfaceFormData: Partial<TlsProfile>,
): FormTemplateSection {

  let adaptedTemplate = templateSection;

  if (interfaceFormData.id != null) {
    adaptedTemplate = adaptIdFieldByFieldName(
      adaptedTemplate,
      interfaceFormData.id,
      'id',
    );
  }

  return adaptedTemplate;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

function adaptIdFieldByFieldName(
  templateSection: FormTemplateSection,
  networkId: string,
  fieldName: 'id',
): FormTemplateSection {
  const options = templateSection.fields[fieldName]?.options;
  if (options == null || networkId === '') {
    return templateSection;
  }

  const netIdOption = options.find(({ value }) => value === networkId);
  if (netIdOption != null) {
    return templateSection;
  }

  const clonedTemplateSection = cloneDeep(templateSection);

  clonedTemplateSection.fields[fieldName].options?.push({
    label: 'Network ID not found',
    value: networkId,
  });

  return clonedTemplateSection;
}

export function adaptTemplateOnFieldUpdate(
  template: FormTemplate,
  fieldName: string,
  value: string,
  tlsProfile: TlsProfile,
  selectedTlsType?: string,
  selectedAuthenType?: string,
  distributors?: Distributor[],
  enabledAccessFunctions?: SupportedFeaturesAccessFunctions,
  templates?: FormTemplateMapByKey,
  mode?: 'create' | 'edit'
): FormTemplate {
  let clonedTemplate = cloneDeep(template);

  clonedTemplate = adaptTlsVersionOptionsBasedOnInterfaceType(
    clonedTemplate,
    fieldName,
    value,
    tlsProfile.tlsVersion,
    mode
  );

  clonedTemplate = adaptTlsHostnameBasedOnTlsType(
    clonedTemplate,
    fieldName,
    value,
    tlsProfile.tlsVersion,
    selectedTlsType,
    selectedAuthenType
  );

  const tlsType = tlsProfile.tlsType?.toLocaleLowerCase();
  const authMode = tlsProfile.authenType?.toLocaleLowerCase();

  if (clonedTemplate.main?.fields) {
    if (
      clonedTemplate.main.fields.privateKeyFile.validation &&
      clonedTemplate.main.fields.certificateFile.validation
    ) {
      if (tlsType === 'client' && authMode === 'simple') {
        clonedTemplate.main.fields.privateKeyFile.validation.required = false;
        clonedTemplate.main.fields.certificateFile.validation.required = false;
      } else {
        clonedTemplate.main.fields.privateKeyFile.validation.required = true;
        clonedTemplate.main.fields.certificateFile.validation.required = true;
      }
    }

    if (clonedTemplate.main.fields.certificateAuthority.validation) {
      if (tlsType === 'server' && authMode === 'simple') {
        clonedTemplate.main.fields.certificateAuthority.validation.required = false;
      } else {
        clonedTemplate.main.fields.certificateAuthority.validation.required = true;
      }
    }

    if (clonedTemplate.main.fields.passphrase.validation) {
      if (authMode === 'mutual') {
        clonedTemplate.main.fields.passphrase.validation.required = true;
      } else {
        clonedTemplate.main.fields.passphrase.validation.required = false;
      }
    }

    const formTemplateSection = clonedTemplate.main as FormTemplateSection;

    if (tlsProfile.catype === 'XCCA') {
      setFormFieldParams(formTemplateSection.fields.certificateFile, { disabled: true, validationRequired: false });
      setFormFieldParams(formTemplateSection.fields.certificateAuthority, { disabled: true, validationRequired: false });
      setFormFieldParams(formTemplateSection.fields.certificateDirectory, { disabled: true, validationRequired: false });
      setFormFieldParams(formTemplateSection.fields.privateKeyFile, { disabled: true, validationRequired: false });
      setFormFieldParams(formTemplateSection.fields.passphrase, { disabled: true, validationRequired: false });
      setFormFieldParams(formTemplateSection.fields.xccaCertParam, { disabled: false, validationRequired: false, hide: 'false' });
      setFormFieldParams(formTemplateSection.fields.commonName, { disabled: false, validationRequired: false, hide: 'false' });
      setFormFieldParams(formTemplateSection.fields.san, { disabled: false, validationRequired: false, hide: 'false' });
      XCCA_FIELDS_TO_SHOW.forEach((fName) => {
        if (formTemplateSection.fields[fName]) {
          formTemplateSection.fields[fName].hide = 'false';
        }
      });
    } else {
      setFormFieldParams(formTemplateSection.fields.certificateFile, { disabled: false });
      setFormFieldParams(formTemplateSection.fields.certificateAuthority, { disabled: false });
      setFormFieldParams(formTemplateSection.fields.certificateDirectory, { disabled: false });
      setFormFieldParams(formTemplateSection.fields.privateKeyFile, { disabled: false });
      setFormFieldParams(formTemplateSection.fields.passphrase, { disabled: false });
      setFormFieldParams(formTemplateSection.fields.xccaCertParam, { disabled: false, validationRequired: false, hide: 'true' });
      setFormFieldParams(formTemplateSection.fields.commonName, { disabled: false, validationRequired: false, hide: 'true' });
      setFormFieldParams(formTemplateSection.fields.san, { disabled: false, validationRequired: false, hide: 'true' });
      XCCA_FIELDS_TO_SHOW.forEach((fName) => {
        if (formTemplateSection.fields[fName]) {
          formTemplateSection.fields[fName].hide = 'true';
        }
      });
    }

    if (
      formTemplateSection.metadata.layout &&
      _.isArray(formTemplateSection.metadata.layout.rows)
    ) {
      formTemplateSection.metadata.layout.rows[1] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[1], 3);
      formTemplateSection.metadata.layout.rows[2] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[2], 3);
    }

    adaptAFTypeOptionsOnFieldUpdate(
      enabledAccessFunctions as SupportedFeaturesAccessFunctions,
      templates as FormTemplateMapByKey,
      formTemplateSection,
    );
  }

  clonedTemplate.key = Date.now().toString();
  clonedTemplate.formId = Date.now().toString();

  return clonedTemplate;
}

export function setFormFieldParams(
  field: FormTemplateField,
  {
    disabled,
    validationRequired,
    hide
  }: { disabled?: boolean; validationRequired?: boolean, hide?: 'true' | 'false' }
) {
  if (disabled !== undefined) {
    field.disabled = disabled;
  }

  if (validationRequired !== undefined) {
    if (field.validation) {
      field.validation.required = validationRequired;
    }
  }

  if (hide !== undefined) {
    field.hide = hide;
  }
}

export function padRow(
  fields: FormTemplateFieldMap,
  row: string[],
  maxLength: number) {
  const nonEmptyFields = (row ?? []).filter((x) => x !== '$empty').slice();
  const visibleFields = (nonEmptyFields).filter((x) => fields[x]?.hide !== 'true');
  if (visibleFields.length < maxLength) {
    for (let ind = 0; ind < maxLength - visibleFields.length; ind += 1) {
      nonEmptyFields.push('$empty');
    }
  }
  return nonEmptyFields;
}

function adaptAFTypeOptionsOnFieldUpdate(
  enabledAccessFunctions: SupportedFeaturesAccessFunctions,
  templates: FormTemplateMapByKey,
  formTemplateSection: FormTemplateSection,
) {
  const tagOptions = getTagOptions(
    templates ?? {},
    enabledAccessFunctions ?? {}
  );
  formTemplateSection.fields.aftype.options = [
    {
      label: 'Select an AF Type',
      value: ''
    },
    ...tagOptions
  ];
}

export function adaptTemplateOnLoad(template: FormTemplate, tlsProfile: TlsProfile): FormTemplate {
  if (template == null) {
    return template;
  }
  const clonedTemplate = cloneDeep(template);
  const formTemplateSection = clonedTemplate.main as FormTemplateSection;

  if (tlsProfile.catype === 'XCCA') {
    setFormFieldParams(formTemplateSection.fields.certificateFile, { disabled: true, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.certificateAuthority, { disabled: true, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.certificateDirectory, { disabled: true, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.privateKeyFile, { disabled: true, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.passphrase, { disabled: true, validationRequired: false });
    XCCA_FIELDS_TO_SHOW.forEach((fName) => {
      if (clonedTemplate.main?.fields[fName]) {
        clonedTemplate.main.fields[fName].hide = 'false';
      }
    });
  } else {
    setFormFieldParams(formTemplateSection.fields.certificateFile, { disabled: false, validationRequired: true });
    setFormFieldParams(formTemplateSection.fields.certificateAuthority, { disabled: false, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.certificateDirectory, { disabled: false, validationRequired: false });
    setFormFieldParams(formTemplateSection.fields.privateKeyFile, { disabled: false, validationRequired: true });
    setFormFieldParams(formTemplateSection.fields.passphrase, { disabled: false, validationRequired: false });
    XCCA_FIELDS_TO_SHOW.forEach((fName) => {
      if (clonedTemplate.main?.fields[fName]) {
        clonedTemplate.main.fields[fName].hide = 'true';
      }
    });
  }
  if (
    formTemplateSection.metadata.layout &&
    _.isArray(formTemplateSection.metadata.layout.rows)
  ) {
    formTemplateSection.metadata.layout.rows[1] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[1], 3);
    formTemplateSection.metadata.layout.rows[2] = padRow(formTemplateSection.fields, formTemplateSection.metadata.layout.rows[2], 3);
  }
  clonedTemplate.formId = Date.now().toString();
  return clonedTemplate;
}

function adaptTlsVersionOptionsBasedOnInterfaceType(
  template: FormTemplate,
  fieldName: string,
  value: string,
  currentTlsVersion?: string,
  mode?: 'create' | 'edit',
): FormTemplate {
  if (fieldName !== 'intfType') {
    return template;
  }
  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    return clonedTemplate;
  }

  if (value === 'DTLS') {
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.initial = 'DTLSv1';
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.options = DTLSVersionsOptions;
  } else {
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.initial = (
      currentTlsVersion === 'DTLSv1' ||
      currentTlsVersion === 'DTLSv1.2' ||
      currentTlsVersion === 'DTLSv1.3'
    )
      ? 'TLSv1.2'
      : currentTlsVersion;
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.options = TLSversionsOptions;
  }
  clonedTemplate.formId = Date.now().toString();
  return clonedTemplate;
}

export function adaptFormData(
  data: TlsProfile,
  template: FormTemplate,
  fieldName: string,
  value: string,
): TlsProfile {
  let clonedData = cloneDeep(data);
  clonedData = adaptTlsVersionBasedOnInterfaceType(clonedData, fieldName, value);
  clonedData = adaptTlsHostNameBasedOnTlsType(clonedData, fieldName, value);
  clonedData = adaptAfType(clonedData, template, fieldName, value);

  if (fieldName === 'catype' && value === 'XCCA') {
    clonedData.certificateAuthority = '';
    clonedData.certificateDirectory = '';
    clonedData.certificateFile = '';
    clonedData.serialnumber = '';
    clonedData.privateKeyFile = '';
  }
  return clonedData;
}

function adaptTlsVersionBasedOnInterfaceType(
  data: TlsProfile,
  fieldName: string,
  value: string,
): TlsProfile {
  if (fieldName !== 'intfType') {
    return data;
  }
  const clonedData = cloneDeep(data);
  if (value === 'DTLS') {
    clonedData.tlsVersion = 'DTLSv1';
  } else if (clonedData.tlsVersion === 'DTLSv1' || clonedData.tlsVersion === 'DTLSv1.2' || clonedData.tlsVersion === 'DTLSv1.3') {
    clonedData.tlsVersion = 'TLSv1.2';
  }
  return clonedData;
}

function adaptAfType(
  data: TlsProfile,
  template: FormTemplate,
  fieldName: string,
  value: string,
): TlsProfile {
  if (fieldName !== 'catype') {
    return data;
  }

  const clonedData = cloneDeep(data);
  if (value === 'SELFSIGNED') {
    clonedData.aftype = '';
  }
  return clonedData;
}

function adaptTlsHostNameBasedOnTlsType(
  data: TlsProfile,
  fieldName: string,
  value: string,
): TlsProfile {
  if (fieldName !== 'tlsType') {
    return data;
  }
  const clonedData = cloneDeep(data);
  if (value?.toLocaleLowerCase() === 'server') {
    clonedData.hostname = '';
  }

  return clonedData;
}

export function adaptInitialTlsVersionOptions(
  template: FormTemplate,
  intfType?: string,
  tlsVersion?: string,
): FormTemplate {

  const clonedTemplate = cloneDeep(template);
  (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.initial = tlsVersion;

  if (intfType === 'DTLS') {
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.options = DTLSVersionsOptions;
  } else {
    (clonedTemplate.main as FormTemplateSection).fields.tlsVersion.options = TLSversionsOptions;
  }

  clonedTemplate.formId = Date.now().toString();
  clonedTemplate.key = Date.now().toString();
  return clonedTemplate;
}

export function adaptTlsProfileFormData(
  tlsProfile: TlsProfile,
): TlsProfile {

  const clonedTlsProfile = cloneDeep(tlsProfile);

  if (clonedTlsProfile.depth?.toLocaleLowerCase() === 'none') {
    clonedTlsProfile.depth = '';
  }

  if (clonedTlsProfile.tlsType?.toLocaleLowerCase() === 'server') {
    clonedTlsProfile.hostname = '';
  }

  return clonedTlsProfile;
}

function adaptTlsHostnameBasedOnTlsType(
  template: FormTemplate,
  fieldName: string,
  value: string,
  currentTlsVersion?: string,
  selectedTlsType?: string,
  selectedAuthenType?: string,
): FormTemplate {
  if (fieldName !== 'tlsType' && fieldName !== 'authenType') {
    return template;
  }
  const clonedTemplate = cloneDeep(template);

  const newValue = value?.toLocaleLowerCase();

  if ((newValue === 'mutual')
    || (newValue === 'simple' && selectedTlsType?.toLocaleLowerCase() === 'client')
    || (newValue === 'client')
    || (newValue === 'server' && selectedAuthenType?.toLocaleLowerCase() === 'mutual')) {
    (clonedTemplate.main as FormTemplateSection).fields.hostname.disabled = false;
  } else {
    (clonedTemplate.main as FormTemplateSection).fields.hostname.disabled = true;
    (clonedTemplate.main as FormTemplateSection).fields.hostname.initial = '';
  }

  clonedTemplate.formId = Date.now().toString();

  return clonedTemplate;
}

const DTLSVersionsOptions = [
  {
    label: 'DTLSv1',
    value: 'DTLSv1',
  },
  {
    label: 'DTLSv1.2',
    value: 'DTLSv1.2',
  },
  {
    label: 'DTLSv1.3',
    value: 'DTLSv1.3',
  },
];

const TLSversionsOptions = [
  {
    label: 'SSLv3',
    value: 'SSLv3',
  },
  {
    label: 'TLSv1',
    value: 'TLSv1',
  },
  {
    label: 'TLSv1.1',
    value: 'TLSv1.1',
  },
  {
    label: 'TLSv1.2',
    value: 'TLSv1.2',
  },
  {
    label: 'TLSv1.3',
    value: 'TLSv1.3',
  },
];

const TLSintfTypeOptions = [
  {
    label: 'HTTPS',
    value: 'HTTPS',
  },
  {
    label: 'FTPS',
    value: 'FTPS',
  },
  {
    label: 'TLS',
    value: 'TLS',
  },
];

const DTLSintfTypeOptions = [
  {
    label: 'DTLS',
    value: 'DTLS',
  },
];
